import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, HeartPulse, Leaf, Sprout, ShieldCheck, Rocket } from "lucide-react";
import { hackathon, registrationState } from "../data/hackathon";
import { useCountdown } from "./hackathon/ui";
import { tone } from "./hackathon/hk";
import yuvasetuLogo from "../assets/hackathon/yuvasetu-logo.webp";
import "./HackathonBanner.scss";

// ─── YUVASETU band, directly under the CoGrad hero ───────────────────────────
// Built in the /hackathon hero's own language — YUVASETU logo, saffron
// tagline, the same labelled facts strip, the same blue registration card —
// so arriving on the microsite feels like the same page continuing.
//
// What it leaves out of that hero, on purpose: the full-viewport height and the
// decorative layer (mesh glow, bridge arcs, floating tiles, orbit). This sits
// immediately below CoGrad's own hero, and a second decorated full-screen hero
// would fight it. Identity carries the recognition; decoration doesn't.
//
// The right column answers the question the left one raises — "what would we
// actually build?" — with the six tracks. The microsite renders those as large
// tilted index cards with tape and a flip; at this size that reads as clutter,
// so these are flat tone tiles using the same TONES palette via `tone(i)`.
//
// Every value is read from src/data/hackathon.js. Note the ₹1.75 L figure in
// that file belongs to AI Eduthon 2025, a past programme — YUVASETU's own top
// prize is prizes.podium[0].

const { registration: reg, eligibility, prizes, finale } = hackathon;
const EASE = [0.2, 0.7, 0.1, 1];

const rise = (delay, y = 22) => ({
  initial: { opacity: 0, y },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: EASE, delay },
});

// Same icon per track id as the microsite's TracksSection, so a student who
// scrolls through to /hackathon sees the same six marks.
const ICONS = {
  education:   GraduationCap,
  health:      HeartPulse,
  environment: Leaf,
  agriculture: Sprout,
  safety:      ShieldCheck,
  open:        Rocket,
};

const TRACKS = hackathon.tracks.map((t) => ({ ...t, Icon: ICONS[t.id] }));

// Copy per registration state. `registrationState()` is the canonical helper —
// it also honours ?reg=upcoming|open|closed so each state can be previewed.
const COPY = {
  upcoming: {
    title: <>Registration opens <span>{reg.opensShort}</span></>,
    cta:   { label: "Explore YUVASETU", href: "/hackathon" },
    link:  { label: "Read the FAQs",    href: "/hackathon#faq" },
  },
  open: {
    title: "Registration Open",
    cta:   { label: "New Registration", href: reg.path },
    link:  { label: "Read the details", href: "/hackathon" },
  },
  closed: {
    title: "Registration Closed",
    cta:   { label: "View YUVASETU", href: "/hackathon" },
    link:  null,
  },
};

// Inside the blue card, so the urgency sits next to the button that acts on it.
function Countdown() {
  const left = useCountdown(reg.opensAt);
  if (registrationState() !== "upcoming" || !left) return null;

  const cells = [["Days", left.days], ["Hrs", left.hours], ["Min", left.minutes], ["Sec", left.seconds]];
  return (
    <div
      className="hbn__count"
      role="timer"
      aria-label={`${left.days} days ${left.hours} hours until registrations open`}
    >
      {cells.map(([k, v]) => (
        <span key={k}>
          <b>{String(v).padStart(2, "0")}</b>
          <small>{k}</small>
        </span>
      ))}
    </div>
  );
}

export default function HackathonBanner() {
  const state = registrationState();
  const copy = COPY[state] || COPY.upcoming;

  return (
    <section className="hbn" aria-labelledby="hbn-title">
      <div className="hbn__inner">

        {/* ── Left: event identity, the facts, the action ─────────────────── */}
        <div className="hbn__brand">
          <motion.h2 className="hbn__h2" id="hbn-title" {...rise(0)}>
            <img
              src={yuvasetuLogo}
              alt={`${hackathon.eventName} — ${hackathon.title}`}
              className="hbn__logo"
              width="1200"
              height="332"
              loading="lazy"
            />
          </motion.h2>

          <motion.p className="hbn__tagline" {...rise(0.08)}>
            <span className="hbn__tag-a">Young Minds.</span>{" "}
            <span className="hbn__tag-b">Real-World Solutions.</span>
          </motion.p>

          <motion.ul className="hbn__facts" aria-label="Key details" {...rise(0.14)}>
            <li><small>For</small>Class {eligibility.classes}</li>
            <li><small>Eligibility</small>{eligibility.schools}</li>
            <li><small>Fee</small>{reg.feeLabel}<em>/student</em></li>
          </motion.ul>

          <motion.div className="hbn__reg" {...rise(0.2, 28)}>
            <p className="hbn__reg-title">{copy.title}</p>

            <Countdown />

            <div className="hbn__reg-actions">
              <Link to={copy.cta.href} className="hbn__reg-btn">{copy.cta.label}</Link>
              {copy.link && (
                <Link to={copy.link.href} className="hbn__reg-link">{copy.link.label}</Link>
              )}
            </div>

            <p className="hbn__reg-foot">
              {prizes.podium[0].value} top prize &middot; Grand Finale in{" "}
              {finale.city}, {finale.date}
            </p>
          </motion.div>
        </div>

        {/* ── Right: what a team would actually build ─────────────────────── */}
        <motion.div className="hbn__tracks" {...rise(0.16, 26)}>
          <p className="hbn__tracks-label">Six challenge tracks</p>

          <ul className="hbn__tracks-grid">
            {TRACKS.map((t, i) => (
              <motion.li
                key={t.id}
                className="hbn__track"
                style={tone(i)}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, ease: EASE, delay: 0.2 + i * 0.05 }}
              >
                <span className="hbn__track-icon" aria-hidden="true">
                  <t.Icon strokeWidth={1.8} />
                </span>
                <span className="hbn__track-n" aria-hidden="true">{t.n}</span>
                <span className="hbn__track-name">{t.name}</span>
              </motion.li>
            ))}
          </ul>

          <p className="hbn__tracks-foot">
            Pick one track, find a problem inside it, and build the fix.
          </p>
        </motion.div>

      </div>
    </section>
  );
}
